import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Trophy,
  Users,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  Activity,
  ChevronRight,
  RefreshCw,
  Loader2,
  Star,
  Target,
  Shield,
  Heart,
  ArrowLeft,
  ExternalLink,
  Search,
  X,
  BarChart3,
  History,
  Zap,
  Calendar,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Legend, Area, AreaChart, ComposedChart
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sport icons by league type
const SPORT_ICONS = {
  basketball: '🏀',
  football: '🏈',
  baseball: '⚾',
  hockey: '🏒',
  soccer: '⚽',
  default: '🎯'
};

// Get icon based on sport type
const getLeagueIcon = (sport) => {
  const sportLower = (sport || '').toLowerCase();
  return SPORT_ICONS[sportLower] || SPORT_ICONS.default;
};

export default function Stats() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [selectedLeague, setSelectedLeague] = useState('nba');
  const [activeTab, setActiveTab] = useState('standings');
  const [loading, setLoading] = useState(true);
  
  // Dynamic leagues from API
  const [leagues, setLeagues] = useState([]);
  
  // Data states
  const [standings, setStandings] = useState([]);
  const [teams, setTeams] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [teamDetails, setTeamDetails] = useState(null);
  const [teamRoster, setTeamRoster] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerStats, setPlayerStats] = useState(null);
  const [playerLoading, setPlayerLoading] = useState(false);
  
  // Search states
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchType, setSearchType] = useState('teams');
  
  // Team/Player history for AI learning
  const [teamHistory, setTeamHistory] = useState(null);
  const [teamStrength, setTeamStrength] = useState(null);
  const [playerHistory, setPlayerHistory] = useState(null);
  
  // Win/Loss record modal state
  const [recordModal, setRecordModal] = useState({ open: false, team: null, type: 'all', data: null, loading: false });

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('token')}`,
    'Content-Type': 'application/json'
  });

  // Fetch leagues on mount
  useEffect(() => {
    const fetchLeagues = async () => {
      try {
        const response = await fetch(`${API_URL}/api/leagues`);
        if (response.ok) {
          const data = await response.json();
          setLeagues(data.map(l => ({
            id: l.league_id,
            name: l.abbreviation || l.name,
            fullName: l.name,
            icon: getLeagueIcon(l.sport)
          })));
        }
      } catch (err) {
        console.error('Failed to fetch leagues:', err);
      }
    };
    fetchLeagues();
  }, []);

  // Fetch data when league changes - ALL IN PARALLEL
  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [standingsRes, teamsRes, injuriesRes, leadersRes] = await Promise.all([
          fetch(`${API_URL}/api/stats/standings/${selectedLeague}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/stats/teams/${selectedLeague}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/stats/injuries/${selectedLeague}`, { headers: getAuthHeaders() }),
          fetch(`${API_URL}/api/stats/leaders/${selectedLeague}`, { headers: getAuthHeaders() })
        ]);
        
        if (standingsRes.ok) {
          const data = await standingsRes.json();
          setStandings(data.standings || []);
        }
        if (teamsRes.ok) {
          const data = await teamsRes.json();
          setTeams(data.teams || []);
        }
        if (injuriesRes.ok) {
          const data = await injuriesRes.json();
          setInjuries(data.injuries || []);
        }
        if (leadersRes.ok) {
          const data = await leadersRes.json();
          setLeaders(data.leaders || []);
        }
      } catch (err) {
        console.error('Failed to fetch stats:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [selectedLeague]);

  // Navigate to game detail with return context
  const handleGameClick = (game) => {
    if (game.game_id && game.status === 'STATUS_FINAL') {
      // Save current context for back navigation
      const returnState = {
        returnTo: location.pathname,
        teamId: selectedTeam,
        leagueId: selectedLeague,
        tab: activeTab
      };
      navigate(`/game/${game.game_id}`, { state: returnState });
    } else if (game.status !== 'STATUS_FINAL') {
      toast.info('Game analysis available after the game ends');
    } else {
      toast.error('Game details not available');
    }
  };
  
  // Restore state when returning from game detail
  useEffect(() => {
    const state = location.state;
    if (state?.teamId) {
      setSelectedTeam(state.teamId);
    }
    if (state?.leagueId) {
      setSelectedLeague(state.leagueId);
    }
    if (state?.tab) {
      setActiveTab(state.tab);
    }
    // NEW: Handle incoming player from GameDetail H2H tab
    if (state?.openPlayer && state?.playerId) {
      const playerId = state.playerId;
      const playerName = state.playerName || 'Player';
      const leagueId = state.leagueId || selectedLeague;
      
      // Set selected player immediately
      setSelectedPlayer({
        id: playerId,
        name: playerName,
        team: state.teamName || ''
      });
      
      // Fetch player stats
      const fetchStats = async () => {
        setPlayerLoading(true);
        try {
          const response = await fetch(`${API_URL}/api/stats/player/${leagueId}/${playerId}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json'
            }
          });
          if (response.ok) {
            const data = await response.json();
            setPlayerStats(data);
          } else {
            toast.error('Failed to load player stats');
          }
        } catch (err) {
          console.error('Failed to fetch player stats:', err);
          toast.error('Failed to load player stats');
        } finally {
          setPlayerLoading(false);
        }
      };
      fetchStats();
    }
  }, [location.state]);

  const fetchStandings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/standings/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStandings(data.standings || []);
      }
    } catch (err) {
      console.error('Failed to fetch standings:', err);
    }
  }, [selectedLeague]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/stats/teams/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams || []);
      }
    } catch (err) {
      console.error('Failed to fetch teams:', err);
    }
  }, [selectedLeague]);

  const fetchInjuries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stats/injuries/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setInjuries(data.injuries || []);
      }
    } catch (err) {
      console.error('Failed to fetch injuries:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLeague]);

  const fetchLeaders = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stats/leaders/${selectedLeague}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setLeaders(data.leaders || []);
      }
    } catch (err) {
      console.error('Failed to fetch leaders:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedLeague]);

  const fetchTeamDetails = async (teamId) => {
    setSelectedTeam(teamId);
    setSelectedPlayer(null); // Reset player selection
    setPlayerStats(null);
    try {
      const [detailsRes, rosterRes] = await Promise.all([
        fetch(`${API_URL}/api/stats/team/${selectedLeague}/${teamId}`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stats/players/${selectedLeague}/${teamId}`, { headers: getAuthHeaders() })
      ]);
      
      if (detailsRes.ok) {
        const data = await detailsRes.json();
        setTeamDetails(data);
      }
      if (rosterRes.ok) {
        const data = await rosterRes.json();
        setTeamRoster(data.players || []);
      }
    } catch (err) {
      console.error('Failed to fetch team details:', err);
    }
  };

  const fetchPlayerStats = async (playerId, playerName) => {
    setSelectedPlayer({ id: playerId, name: playerName });
    setPlayerLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/stats/player/${selectedLeague}/${playerId}`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPlayerStats(data);
      } else {
        toast.error('Failed to load player stats');
      }
    } catch (err) {
      console.error('Failed to fetch player stats:', err);
      toast.error('Failed to load player stats');
    } finally {
      setPlayerLoading(false);
    }
  };

  const closePlayerModal = () => {
    setSelectedPlayer(null);
    setPlayerStats(null);
  };

  // Search functions
  const handleSearch = async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const endpoint = searchType === 'teams' 
        ? `${API_URL}/api/stats/search/teams?query=${encodeURIComponent(query)}&league_id=${selectedLeague}`
        : `${API_URL}/api/stats/search/players?query=${encodeURIComponent(query)}&league_id=${selectedLeague}`;
      
      const response = await fetch(endpoint, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  };
  
  // Debounced search - wrapped handleSearch in useCallback to fix stale closure
  const handleSearchCallback = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const endpoint = searchType === 'teams' 
        ? `${API_URL}/api/stats/search/teams?query=${encodeURIComponent(query)}&league_id=${selectedLeague}`
        : `${API_URL}/api/stats/search/players?query=${encodeURIComponent(query)}&league_id=${selectedLeague}`;
      
      const response = await fetch(endpoint, { headers: getAuthHeaders() });
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results || []);
      }
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearchLoading(false);
    }
  }, [searchType, selectedLeague]);

  useEffect(() => {
    const timer = setTimeout(() => {
      handleSearchCallback(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, handleSearchCallback]);
  
  // Fetch team history and strength for AI learning
  const fetchTeamAIData = async (teamId) => {
    try {
      const [historyRes, strengthRes] = await Promise.all([
        fetch(`${API_URL}/api/stats/team/${selectedLeague}/${teamId}/history`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/stats/team/${selectedLeague}/${teamId}/strength`, { headers: getAuthHeaders() })
      ]);
      
      if (historyRes.ok) {
        const data = await historyRes.json();
        setTeamHistory(data);
      }
      if (strengthRes.ok) {
        const data = await strengthRes.json();
        setTeamStrength(data);
      }
    } catch (err) {
      console.error('Failed to fetch team AI data:', err);
    }
  };
  
  // Fetch player history for AI learning
  const fetchPlayerAIData = async (playerId) => {
    try {
      const response = await fetch(`${API_URL}/api/stats/player/${selectedLeague}/${playerId}/history`, { 
        headers: getAuthHeaders() 
      });
      if (response.ok) {
        const data = await response.json();
        setPlayerHistory(data);
      }
    } catch (err) {
      console.error('Failed to fetch player AI data:', err);
    }
  };
  
  // Handle search result click
  const handleSearchResultClick = async (result) => {
    setSearchQuery('');
    setSearchResults([]);
    
    if (result.type === 'team') {
      // Switch to the team's league if different
      if (result.league_id !== selectedLeague) {
        setSelectedLeague(result.league_id);
      }
      // PERFORMANCE: Fetch team details and AI data in parallel
      await Promise.all([
        fetchTeamDetails(result.team_id),
        fetchTeamAIData(result.team_id)
      ]);
      setActiveTab('teams');
    } else if (result.type === 'player') {
      // Switch to the player's league if different
      if (result.league_id !== selectedLeague) {
        setSelectedLeague(result.league_id);
      }
      // PERFORMANCE: Fetch player stats and AI data in parallel
      await Promise.all([
        fetchPlayerStats(result.player_id, result.name),
        fetchPlayerAIData(result.player_id)
      ]);
    }
  };
  
  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
  };
  
  // Fetch team win/loss record details
  const fetchRecordDetails = async (team, recordType = 'all') => {
    setRecordModal({ open: true, team, type: recordType, data: null, loading: true });
    try {
      const response = await fetch(
        `${API_URL}/api/stats/team/${selectedLeague}/${team.team_id}/record-details?record_type=${recordType}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setRecordModal(prev => ({ ...prev, data, loading: false }));
      } else {
        toast.error('Failed to load record details');
        setRecordModal(prev => ({ ...prev, loading: false }));
      }
    } catch (err) {
      console.error('Failed to fetch record details:', err);
      toast.error('Failed to load record details');
      setRecordModal(prev => ({ ...prev, loading: false }));
    }
  };
  
  // Handle injury click - navigate to player stats
  const handleInjuryClick = async (injury) => {
    if (injury.player_id) {
      // PERFORMANCE: Fetch player stats and AI data in parallel
      await Promise.all([
        fetchPlayerStats(injury.player_id, injury.player_name),
        fetchPlayerAIData(injury.player_id)
      ]);
    } else {
      toast.info('Player details not available');
    }
  };


  // Group standings by conference/division
  const groupedStandings = standings.reduce((acc, team) => {
    const group = team.group || 'League';
    if (!acc[group]) acc[group] = [];
    acc[group].push(team);
    return acc;
  }, {});

  // Group leaders by category
  const groupedLeaders = leaders.reduce((acc, leader) => {
    const cat = leader.category || 'General';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(leader);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <main className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold mb-2">Stats Center</h1>
            <p className="text-muted-foreground">Team standings, player stats, and injury reports</p>
          </div>
          
          <Select value={selectedLeague} onValueChange={setSelectedLeague}>
            <SelectTrigger className="w-[200px] mt-4 md:mt-0">
              <SelectValue placeholder="Select League" />
            </SelectTrigger>
            <SelectContent>
              {leagues.map(league => (
                <SelectItem key={league.id} value={league.id}>
                  <span className="mr-2">{league.icon}</span>
                  {league.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Search Section */}
        <div className="mb-6">
          <div className="flex gap-2 items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={`Search ${searchType === 'teams' ? 'teams' : 'players'}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
                data-testid="stats-search-input"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
            <Select value={searchType} onValueChange={setSearchType}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="teams">
                  <span className="flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Teams
                  </span>
                </SelectItem>
                <SelectItem value="players">
                  <span className="flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Players
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {/* Search Results Dropdown */}
          {searchQuery && searchResults.length > 0 && (
            <div className="mt-2 border border-border rounded-lg bg-card shadow-lg max-h-80 overflow-y-auto">
              {searchLoading ? (
                <div className="p-4 flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : (
                searchResults.map((result, idx) => (
                  <button
                    key={`${result.type}-${result.team_id || result.player_id}-${idx}`}
                    onClick={() => handleSearchResultClick(result)}
                    className="w-full p-3 flex items-center gap-3 hover:bg-secondary/50 border-b border-border last:border-0 text-left"
                    data-testid={`search-result-${idx}`}
                  >
                    {result.logo || result.headshot || result.team_logo ? (
                      <img 
                        src={result.logo || result.headshot || result.team_logo} 
                        alt="" 
                        className="w-10 h-10 rounded-full object-cover bg-secondary"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                        {result.type === 'team' ? <Users className="w-5 h-5" /> : <Star className="w-5 h-5" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{result.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {result.type === 'team' 
                          ? `${result.abbreviation} • ${result.league_name}`
                          : `${result.position} • ${result.team_name || result.league_name}`
                        }
                      </p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {result.type === 'team' ? 'Team' : 'Player'}
                    </Badge>
                  </button>
                ))
              )}
            </div>
          )}
          
          {searchQuery && searchResults.length === 0 && !searchLoading && (
            <div className="mt-2 p-4 text-center text-muted-foreground border border-border rounded-lg">
              No {searchType} found matching "{searchQuery}"
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="standings">
              <Trophy className="w-4 h-4 mr-2" />
              Standings
            </TabsTrigger>
            <TabsTrigger value="teams">
              <Users className="w-4 h-4 mr-2" />
              Teams
            </TabsTrigger>
            <TabsTrigger value="injuries">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Injuries
            </TabsTrigger>
            <TabsTrigger value="leaders">
              <Star className="w-4 h-4 mr-2" />
              Leaders
            </TabsTrigger>
          </TabsList>

          {/* Standings Tab */}
          <TabsContent value="standings">
            {loading ? (
              <div className="space-y-4">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(groupedStandings).map(([group, teams]) => (
                  <Card key={group}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Trophy className="w-5 h-5 text-yellow-500" />
                        {group}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-2">#</th>
                              <th className="text-left py-2 px-2">Team</th>
                              <th className="text-center py-2 px-2">W</th>
                              <th className="text-center py-2 px-2">L</th>
                              <th className="text-center py-2 px-2">PCT</th>
                              <th className="text-center py-2 px-2">GB</th>
                              <th className="text-center py-2 px-2 hidden md:table-cell">Home</th>
                              <th className="text-center py-2 px-2 hidden md:table-cell">Away</th>
                              <th className="text-center py-2 px-2 hidden lg:table-cell">Streak</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teams.map((team, idx) => (
                              <tr 
                                key={team.team_id || idx} 
                                className="border-b border-border/50 hover:bg-secondary/30"
                              >
                                <td className="py-2 px-2 font-medium">{team.rank || idx + 1}</td>
                                <td 
                                  className="py-2 px-2 cursor-pointer hover:bg-secondary/50"
                                  onClick={() => fetchTeamDetails(team.team_id)}
                                  data-testid={`standings-team-${team.team_id}`}
                                >
                                  <div className="flex items-center gap-2">
                                    {team.logo && (
                                      <img src={team.logo} alt="" className="w-6 h-6 object-contain" />
                                    )}
                                    <span className="font-medium hover:text-primary">{team.team_name}</span>
                                  </div>
                                </td>
                                <td 
                                  className="text-center py-2 px-2 text-green-400 cursor-pointer hover:bg-green-500/20 rounded transition-colors font-bold"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchRecordDetails(team, 'wins');
                                  }}
                                  data-testid={`standings-wins-${team.team_id}`}
                                  title="Click to see win details"
                                >
                                  W{team.wins}
                                </td>
                                <td 
                                  className="text-center py-2 px-2 text-red-400 cursor-pointer hover:bg-red-500/20 rounded transition-colors font-bold"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchRecordDetails(team, 'losses');
                                  }}
                                  data-testid={`standings-losses-${team.team_id}`}
                                  title="Click to see loss details"
                                >
                                  L{team.losses}
                                </td>
                                <td className="text-center py-2 px-2">{(team.win_percent * 100).toFixed(1)}%</td>
                                <td className="text-center py-2 px-2">{team.games_back}</td>
                                <td className="text-center py-2 px-2 hidden md:table-cell">{team.home_record}</td>
                                <td className="text-center py-2 px-2 hidden md:table-cell">{team.away_record}</td>
                                <td className="text-center py-2 px-2 hidden lg:table-cell">{team.streak}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Teams Tab */}
          <TabsContent value="teams">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Team List */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Teams</CardTitle>
                  <CardDescription>{teams.length} teams</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-[600px] overflow-y-auto">
                    {teams.map(team => (
                      <div
                        key={team.team_id}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedTeam === team.team_id 
                            ? 'bg-primary/20 border border-primary/50' 
                            : 'bg-secondary/30 hover:bg-secondary/50'
                        }`}
                        onClick={() => fetchTeamDetails(team.team_id)}
                      >
                        <div className="flex items-center gap-3">
                          {team.logo && (
                            <img src={team.logo} alt="" className="w-10 h-10 object-contain" />
                          )}
                          <div>
                            <p className="font-medium">{team.name}</p>
                            <p className="text-xs text-muted-foreground">{team.conference}</p>
                          </div>
                          <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Team Details */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle>
                    {teamDetails ? teamDetails.name : 'Select a Team'}
                  </CardTitle>
                  {teamDetails && (
                    <CardDescription>
                      {teamDetails.record} | {teamDetails.standing}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {teamDetails ? (
                    <div className="space-y-6">
                      {/* Team Info */}
                      <div className="flex items-center gap-4">
                        {teamDetails.logo && (
                          <img src={teamDetails.logo} alt="" className="w-20 h-20 object-contain" />
                        )}
                        <div>
                          <p className="text-lg font-medium">{teamDetails.location}</p>
                          <p className="text-muted-foreground">{teamDetails.venue}</p>
                        </div>
                      </div>

                      {/* Recent Games */}
                      {teamDetails.recent_games?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            Recent Games
                            <span className="text-xs text-muted-foreground font-normal">(Click for game analysis)</span>
                          </h4>
                          <div className="space-y-2">
                            {teamDetails.recent_games.slice(0, 5).map((game) => (
                              <div 
                                key={game.game_id || `${game.date}-${game.opponent}`} 
                                className={`flex items-center justify-between p-3 bg-secondary/30 rounded transition-all ${
                                  game.status === 'STATUS_FINAL' && game.game_id 
                                    ? 'cursor-pointer hover:bg-secondary/50 hover:scale-[1.01] group' 
                                    : 'opacity-70'
                                }`}
                                onClick={() => handleGameClick(game)}
                                data-testid={`recent-game-${game.game_id || game.date}`}
                              >
                                <div className="flex items-center gap-3">
                                  <span className={`font-bold text-lg ${game.result === 'W' ? 'text-green-400' : 'text-red-400'}`}>
                                    {game.result}
                                  </span>
                                  <div className="flex flex-col">
                                    <span className="text-sm font-medium">vs {game.opponent}</span>
                                    <span className="text-xs text-muted-foreground">
                                      {game.date ? new Date(game.date).toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <span className="text-sm font-mono">{game.score}</span>
                                  {game.status === 'STATUS_FINAL' && game.game_id && (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                                  )}
                                  {game.status !== 'STATUS_FINAL' && (
                                    <Badge variant="outline" className="text-xs">
                                      {game.status === 'STATUS_SCHEDULED' ? 'Upcoming' : game.status?.replace('STATUS_', '')}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Roster with Injuries */}
                      {teamRoster.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Roster ({teamRoster.length} players)
                            {teamRoster.some(p => p.injuries?.length > 0) && (
                              <Badge variant="destructive" className="ml-2">
                                {teamRoster.filter(p => p.injuries?.length > 0).length} Injured
                              </Badge>
                            )}
                          </h4>
                          <p className="text-xs text-muted-foreground mb-3">Click on a player to view full stats</p>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto">
                            {teamRoster.map(player => (
                              <div 
                                key={player.player_id} 
                                className={`p-3 rounded cursor-pointer transition-all hover:scale-[1.02] ${
                                  player.injuries?.length > 0 
                                    ? 'bg-red-500/10 border border-red-500/20 hover:bg-red-500/20' 
                                    : 'bg-secondary/30 hover:bg-secondary/50'
                                }`}
                                onClick={() => fetchPlayerStats(player.player_id, player.name)}
                                data-testid={`player-${player.player_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  {player.headshot && (
                                    <img src={player.headshot} alt="" className="w-12 h-12 rounded-full object-cover" />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <p className="font-medium truncate">{player.name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      #{player.jersey} | {player.position}
                                    </p>
                                    {player.stats && (
                                      <p className="text-xs text-primary mt-1">
                                        {player.stats.ppg && `${player.stats.ppg} PPG`}
                                        {player.stats.rpg && ` | ${player.stats.rpg} RPG`}
                                        {player.stats.apg && ` | ${player.stats.apg} APG`}
                                      </p>
                                    )}
                                  </div>
                                  {player.injuries?.length > 0 ? (
                                    <div className="flex flex-col items-end">
                                      <AlertTriangle className="w-4 h-4 text-red-400" />
                                      <span className="text-xs text-red-400">{player.injuries[0]?.status}</span>
                                    </div>
                                  ) : (
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* AI Learning Data Panel */}
                      {(teamHistory || teamStrength) && (
                        <div className="mt-6 border-t border-border pt-6">
                          <h4 className="font-medium mb-4 flex items-center gap-2">
                            <Zap className="w-4 h-4 text-yellow-500" />
                            AI Learning Insights
                          </h4>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Performance Summary */}
                            {teamHistory?.performance_summary && (
                              <div className="p-4 bg-secondary/30 rounded-lg">
                                <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                  <BarChart3 className="w-4 h-4" />
                                  Performance Summary
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Win Rate</span>
                                    <span className="font-medium">{teamHistory.performance_summary.win_rate}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Home Record</span>
                                    <span className="font-medium">{teamHistory.performance_summary.home_record}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Away Record</span>
                                    <span className="font-medium">{teamHistory.performance_summary.away_record}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Avg Scored</span>
                                    <span className="font-medium">{teamHistory.performance_summary.avg_points_scored}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Avg Allowed</span>
                                    <span className="font-medium">{teamHistory.performance_summary.avg_points_allowed}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Point Diff</span>
                                    <span className={`font-medium ${teamHistory.performance_summary.point_differential > 0 ? 'text-green-500' : 'text-red-500'}`}>
                                      {teamHistory.performance_summary.point_differential > 0 ? '+' : ''}{teamHistory.performance_summary.point_differential}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}
                            
                            {/* Team Strength Analysis */}
                            {teamStrength?.strength_analysis && (
                              <div className="p-4 bg-secondary/30 rounded-lg">
                                <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                  <Target className="w-4 h-4" />
                                  Strength Analysis
                                </h5>
                                <div className="space-y-2 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Base Win Rate</span>
                                    <span className="font-medium">{teamStrength.strength_analysis.base_win_rate}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Adjusted Win Rate</span>
                                    <span className="font-medium">{teamStrength.strength_analysis.adjusted_win_rate}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Strength Reduction</span>
                                    <span className="font-medium text-orange-500">-{teamStrength.strength_analysis.strength_reduction}%</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Chemistry Score</span>
                                    <span className="font-medium">{teamStrength.strength_analysis.chemistry_score}/100</span>
                                  </div>
                                </div>
                                {teamStrength.injured_players?.length > 0 && (
                                  <div className="mt-3 pt-3 border-t border-border">
                                    <p className="text-xs text-red-400">
                                      {teamStrength.injured_players.length} player(s) injured affecting team strength
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          
                          {/* Trend Indicator */}
                          {teamHistory?.trend && (
                            <div className={`mt-4 p-3 rounded-lg flex items-center gap-3 ${
                              teamHistory.trend.status === 'hot' ? 'bg-green-500/10 border border-green-500/20' :
                              teamHistory.trend.status === 'cold' ? 'bg-red-500/10 border border-red-500/20' :
                              'bg-secondary/30'
                            }`}>
                              <History className="w-5 h-5" />
                              <div>
                                <p className="font-medium">Last 5 Games: {teamHistory.trend.last_5_record}</p>
                                <p className="text-sm text-muted-foreground">{teamHistory.trend.description}</p>
                              </div>
                            </div>
                          )}
                          
                          {/* Performance Graph */}
                          {teamHistory?.graph_data && teamHistory.graph_data.length > 0 && (
                            <div className="mt-6">
                              <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <BarChart3 className="w-4 h-4" />
                                Scoring Trend (Last 15 Games)
                              </h5>
                              <div className="h-48 bg-secondary/20 rounded-lg p-2">
                                <ResponsiveContainer width="100%" height="100%">
                                  <ComposedChart data={teamHistory.graph_data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                                    <XAxis dataKey="game" tick={{ fontSize: 10 }} stroke="#666" />
                                    <YAxis tick={{ fontSize: 10 }} stroke="#666" />
                                    <Tooltip 
                                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                                      formatter={(value, name) => [value, name === 'scored' ? 'Scored' : 'Allowed']}
                                      labelFormatter={(label) => `Game ${label}`}
                                    />
                                    <Legend />
                                    <Bar dataKey="scored" fill="#22c55e" name="Scored" radius={[2, 2, 0, 0]} />
                                    <Bar dataKey="allowed" fill="#ef4444" name="Allowed" radius={[2, 2, 0, 0]} />
                                    <Line type="monotone" dataKey="margin" stroke="#3b82f6" strokeWidth={2} dot={false} name="Margin" />
                                  </ComposedChart>
                                </ResponsiveContainer>
                              </div>
                            </div>
                          )}
                          
                          {/* Recent Games List */}
                          {teamHistory?.recent_games && teamHistory.recent_games.length > 0 && (
                            <div className="mt-6">
                              <h5 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Recent Games This Season
                              </h5>
                              <div className="space-y-2 max-h-64 overflow-y-auto">
                                {teamHistory.recent_games.slice(0, 10).map((game) => (
                                  <div 
                                    key={game.game_id || `${game.date}-${game.opponent}`}
                                    className={`p-3 rounded-lg border flex items-center justify-between ${
                                      game.result === 'W' ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3">
                                      <Badge variant={game.result === 'W' ? 'default' : 'destructive'} className="w-8 justify-center">
                                        {game.result}
                                      </Badge>
                                      <div>
                                        <p className="font-medium text-sm">
                                          {game.home_away === 'home' ? 'vs' : '@'} {game.opponent}
                                        </p>
                                        <p className="text-xs text-muted-foreground">{game.date}</p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="font-mono text-sm font-bold">{game.score}</span>
                                      {game.espn_url && (
                                        <a 
                                          href={game.espn_url} 
                                          target="_blank" 
                                          rel="noopener noreferrer"
                                          className="text-muted-foreground hover:text-primary"
                                          title="View on ESPN"
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                              {teamHistory.espn_team_url && (
                                <a 
                                  href={teamHistory.espn_team_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="mt-3 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                  View Full Schedule on ESPN
                                </a>
                              )}
                            </div>
                          )}
                          
                          {/* AI Recommendation */}
                          {teamHistory?.ai_recommendation && (
                            <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                              <p className="text-sm">
                                <span className="font-medium text-yellow-500">AI Analysis: </span>
                                {teamHistory.ai_recommendation}
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Load AI Data Button */}
                      {selectedTeam && !teamHistory && !teamStrength && (
                        <div className="mt-6 text-center">
                          <Button 
                            variant="outline" 
                            onClick={() => fetchTeamAIData(selectedTeam)}
                            className="gap-2"
                          >
                            <Zap className="w-4 h-4" />
                            Load AI Learning Data
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>Select a team to view details</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Injuries Tab */}
          <TabsContent value="injuries">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Injury Report
                </CardTitle>
                <CardDescription>
                  {injuries.length} players on injury list • Click any player to view stats
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <Skeleton className="h-64 w-full" />
                ) : injuries.length === 0 ? (
                  <div className="text-center py-12">
                    <Heart className="w-12 h-12 mx-auto mb-4 text-green-500" />
                    <p className="text-lg font-medium text-green-500 mb-2">All Clear!</p>
                    <p className="text-muted-foreground mb-4">No injuries reported for this league</p>
                    <p className="text-sm text-muted-foreground mb-4">
                      Good news - all players are healthy and ready to play!
                    </p>
                    <Button 
                      variant="outline" 
                      onClick={() => setActiveTab('teams')}
                      className="gap-2"
                    >
                      <Users className="w-4 h-4" />
                      Browse Teams & Rosters
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[600px] overflow-y-auto">
                    {injuries.map((injury) => (
                      <div 
                        key={injury.player_id || `${injury.team_id}-${injury.player_name}`} 
                        className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg cursor-pointer hover:bg-red-500/20 transition-colors group"
                        onClick={() => handleInjuryClick(injury)}
                        data-testid={`injury-card-${injury.player_id || injury.player_name}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            {injury.team_logo && (
                              <img src={injury.team_logo} alt="" className="w-10 h-10 object-contain" />
                            )}
                            <div>
                              <p className="font-medium group-hover:text-primary transition-colors">
                                {injury.player_name}
                                <ChevronRight className="inline w-4 h-4 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {injury.position} | {injury.team_name}
                              </p>
                            </div>
                          </div>
                          <Badge variant="destructive">{injury.status}</Badge>
                        </div>
                        <div className="mt-2 text-sm">
                          <p><strong>Injury:</strong> {injury.injury_type}</p>
                          {injury.details && <p className="text-muted-foreground">{injury.details}</p>}
                          {injury.return_date && (
                            <p className="text-green-400 mt-1">Expected Return: {injury.return_date}</p>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          Click to view player stats and history
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Leaders Tab */}
          <TabsContent value="leaders">
            {loading ? (
              <Skeleton className="h-64 w-full" />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {Object.entries(groupedLeaders).map(([category, categoryLeaders]) => (
                  <Card key={category}>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Star className="w-5 h-5 text-yellow-500" />
                        {category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryLeaders.slice(0, 5).map((leader, idx) => (
                          <div key={leader.player_id || `${leader.player_name}-${leader.team_name}`} className="flex items-center gap-3">
                            <span className={`font-bold text-lg w-6 ${
                              idx === 0 ? 'text-yellow-500' : 
                              idx === 1 ? 'text-gray-400' : 
                              idx === 2 ? 'text-amber-600' : 'text-muted-foreground'
                            }`}>
                              {leader.rank || idx + 1}
                            </span>
                            {leader.headshot && (
                              <img src={leader.headshot} alt="" className="w-10 h-10 rounded-full object-cover" />
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{leader.player_name}</p>
                              <p className="text-xs text-muted-foreground truncate">{leader.team_name}</p>
                            </div>
                            <span className="font-bold text-primary">{leader.value}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Player Stats Modal - Enhanced with Graphs and AI Analysis */}
      {selectedPlayer && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="sticky top-0 bg-card z-10 border-b">
              <div className="flex justify-between items-start">
                <div>
                  <CardTitle className="flex items-center gap-3">
                    {playerStats?.headshot && (
                      <img src={playerStats.headshot} alt="" className="w-16 h-16 rounded-full object-cover" />
                    )}
                    <div>
                      <span>{selectedPlayer.name}</span>
                      {playerStats && (
                        <p className="text-sm font-normal text-muted-foreground">
                          #{playerStats.jersey} | {playerStats.position} | {playerStats.team_name}
                        </p>
                      )}
                    </div>
                  </CardTitle>
                </div>
                <div className="flex items-center gap-2">
                  {playerStats?.espn_player_url && (
                    <a 
                      href={playerStats.espn_player_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-muted-foreground hover:text-primary"
                    >
                      <ExternalLink className="w-5 h-5" />
                    </a>
                  )}
                  <Button variant="ghost" size="icon" onClick={closePlayerModal}>
                    <span className="text-xl">×</span>
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {playerLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <span className="ml-3">Loading player stats...</span>
                </div>
              ) : playerStats ? (
                <div className="space-y-6">
                  {/* Player Bio */}
                  {(playerStats.height || playerStats.weight || playerStats.age) && (
                    <div className="grid grid-cols-3 gap-4 text-center">
                      {playerStats.height && (
                        <div className="bg-secondary/30 p-3 rounded-lg">
                          <p className="text-xs text-muted-foreground">Height</p>
                          <p className="font-bold">{playerStats.height}</p>
                        </div>
                      )}
                      {playerStats.weight && (
                        <div className="bg-secondary/30 p-3 rounded-lg">
                          <p className="text-xs text-muted-foreground">Weight</p>
                          <p className="font-bold">{playerStats.weight}</p>
                        </div>
                      )}
                      {playerStats.age && (
                        <div className="bg-secondary/30 p-3 rounded-lg">
                          <p className="text-xs text-muted-foreground">Age</p>
                          <p className="font-bold">{playerStats.age}</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* AI Learning Data & Trend */}
                  {playerStats.ai_learning_data && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="bg-primary/10 p-3 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Impact Score</p>
                        <p className="text-lg font-bold text-primary">{playerStats.ai_learning_data.impact_score}/100</p>
                      </div>
                      <div className="bg-secondary/30 p-3 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Reliability</p>
                        <p className="text-lg font-bold">{playerStats.ai_learning_data.reliability_rating}%</p>
                      </div>
                      <div className="bg-secondary/30 p-3 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Consistency</p>
                        <p className="text-lg font-bold">{playerStats.ai_learning_data.consistency}%</p>
                      </div>
                      <div className={`p-3 rounded-lg text-center ${
                        playerStats.ai_learning_data.trend_direction === 'improving' ? 'bg-green-500/10' :
                        playerStats.ai_learning_data.trend_direction === 'declining' ? 'bg-red-500/10' :
                        'bg-secondary/30'
                      }`}>
                        <p className="text-xs text-muted-foreground">Trend</p>
                        <p className="text-lg font-bold flex items-center justify-center gap-1">
                          {playerStats.ai_learning_data.trend_direction === 'improving' && <TrendingUp className="w-4 h-4 text-green-500" />}
                          {playerStats.ai_learning_data.trend_direction === 'declining' && <TrendingDown className="w-4 h-4 text-red-500" />}
                          {playerStats.ai_learning_data.trend_direction === 'stable' && <Minus className="w-4 h-4" />}
                          {playerStats.ai_learning_data.trend_direction?.charAt(0).toUpperCase() + playerStats.ai_learning_data.trend_direction?.slice(1) || 'N/A'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {/* Trend Analysis Detail */}
                  {playerStats.trend_analysis && (
                    <div className={`p-3 rounded-lg flex items-center gap-3 ${
                      playerStats.trend_analysis.direction === 'improving' ? 'bg-green-500/10 border border-green-500/20' :
                      playerStats.trend_analysis.direction === 'declining' ? 'bg-red-500/10 border border-red-500/20' :
                      'bg-secondary/30'
                    }`}>
                      {playerStats.trend_analysis.direction === 'improving' && <TrendingUp className="w-5 h-5 text-green-500" />}
                      {playerStats.trend_analysis.direction === 'declining' && <TrendingDown className="w-5 h-5 text-red-500" />}
                      {playerStats.trend_analysis.direction === 'stable' && <Minus className="w-5 h-5" />}
                      <div>
                        <p className="font-medium">{playerStats.trend_analysis.description}</p>
                        <p className="text-sm text-muted-foreground">
                          First half avg: {playerStats.trend_analysis.first_half_avg} → Second half avg: {playerStats.trend_analysis.second_half_avg}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Performance Graph */}
                  {playerStats.graph_data && playerStats.graph_data.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Performance Trend (Last 15 Games)
                      </h4>
                      <div className="h-48 bg-secondary/20 rounded-lg p-2">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={playerStats.graph_data}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                            <XAxis dataKey="game" tick={{ fontSize: 10 }} stroke="#666" />
                            <YAxis tick={{ fontSize: 10 }} stroke="#666" />
                            <Tooltip 
                              contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}
                              formatter={(value, name) => [value, name.charAt(0).toUpperCase() + name.slice(1)]}
                            />
                            <Area type="monotone" dataKey="points" stroke="#f43f5e" fill="#f43f5e33" strokeWidth={2} name="Points" />
                            <Area type="monotone" dataKey="rebounds" stroke="#3b82f6" fill="#3b82f633" strokeWidth={2} name="Rebounds" />
                            <Area type="monotone" dataKey="assists" stroke="#22c55e" fill="#22c55e33" strokeWidth={2} name="Assists" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  )}

                  {/* Season Stats */}
                  {playerStats.season_stats && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Activity className="w-4 h-4" />
                        This Season Statistics
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(playerStats.season_stats).slice(0, 12).map(([key, value]) => (
                          <div key={key} className="bg-secondary/30 p-3 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground uppercase">{key}</p>
                            <p className="text-lg font-bold text-primary">
                              {typeof value === 'object' ? value.displayValue || value.value : value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Career Stats */}
                  {playerStats.career_stats && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Trophy className="w-4 h-4" />
                        Career Statistics (All Teams)
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(playerStats.career_stats).slice(0, 12).map(([key, value]) => (
                          <div key={key} className="bg-primary/10 p-3 rounded-lg text-center">
                            <p className="text-xs text-muted-foreground uppercase">{key}</p>
                            <p className="text-lg font-bold">
                              {typeof value === 'object' ? value.displayValue || value.value : value}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Season vs Career Comparison */}
                  {playerStats.season_comparison?.vs_career && Object.keys(playerStats.season_comparison.vs_career).length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Season vs Career Comparison
                      </h4>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        {Object.entries(playerStats.season_comparison.vs_career).map(([stat, data]) => (
                          <div 
                            key={stat} 
                            className={`p-3 rounded-lg text-center ${
                              data.status === 'up' ? 'bg-green-500/10 border border-green-500/20' :
                              data.status === 'down' ? 'bg-red-500/10 border border-red-500/20' :
                              'bg-secondary/30'
                            }`}
                          >
                            <p className="text-xs text-muted-foreground uppercase">{stat}</p>
                            <p className="text-lg font-bold">{data.current}</p>
                            <p className={`text-xs ${data.status === 'up' ? 'text-green-500' : data.status === 'down' ? 'text-red-500' : 'text-muted-foreground'}`}>
                              {data.diff_percent > 0 ? '+' : ''}{data.diff_percent}% vs career
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Game Log */}
                  {playerStats.recent_games?.length > 0 && (
                    <div>
                      <h4 className="font-medium mb-3 flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          Recent Games
                        </span>
                        {playerStats.espn_gamelog_url && (
                          <a 
                            href={playerStats.espn_gamelog_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-muted-foreground hover:text-primary flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            Full Game Log
                          </a>
                        )}
                      </h4>
                      <div className="space-y-2 max-h-[200px] overflow-y-auto">
                        {playerStats.recent_games.slice(0, 10).map((game) => (
                          <div key={game.game_id || `${game.date}-${game.opponent}`} className="flex items-center justify-between p-2 bg-secondary/30 rounded text-sm">
                            <span className="w-20">{game.date}</span>
                            <span className="flex-1">vs {game.opponent}</span>
                            <span className="font-medium text-primary">{game.points || game.stats?.PTS || 0} PTS</span>
                            <span className="text-muted-foreground">{game.rebounds || game.stats?.REB || 0} REB</span>
                            <span className="text-muted-foreground">{game.assists || game.stats?.AST || 0} AST</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* AI Recommendation */}
                  {playerStats.ai_recommendation && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
                      <p className="text-sm">
                        <span className="font-medium text-yellow-500">AI Analysis: </span>
                        {playerStats.ai_recommendation}
                      </p>
                    </div>
                  )}

                  {/* No Stats Available */}
                  {!playerStats.season_stats && !playerStats.career_stats && (
                    <div className="text-center py-8 text-muted-foreground">
                      <Target className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>No detailed statistics available for this player</p>
                      {playerStats.espn_stats_url && (
                        <a 
                          href={playerStats.espn_stats_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-4 inline-flex items-center gap-2 text-sm text-primary hover:underline"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View Stats on ESPN
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Failed to load player stats</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Win/Loss Record Details Modal */}
      <Dialog open={recordModal.open} onOpenChange={(open) => !open && setRecordModal({ open: false, team: null, type: 'all', data: null, loading: false })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {recordModal.data?.team_logo && (
                <img src={recordModal.data.team_logo} alt="" className="w-10 h-10 object-contain" />
              )}
              <div>
                <span>{recordModal.data?.team_name || recordModal.team?.team_name || 'Team'}</span>
                <span className="text-muted-foreground ml-2">
                  {recordModal.type === 'wins' ? 'Wins' : recordModal.type === 'losses' ? 'Losses' : 'Record'} Details
                </span>
              </div>
            </DialogTitle>
            <DialogDescription>
              {recordModal.type === 'wins' 
                ? `All ${recordModal.data?.record_summary?.wins || 0} wins this season`
                : recordModal.type === 'losses'
                ? `All ${recordModal.data?.record_summary?.losses || 0} losses this season`
                : `Season record: ${recordModal.data?.record_summary?.wins || 0}-${recordModal.data?.record_summary?.losses || 0}`
              }
            </DialogDescription>
          </DialogHeader>

          {recordModal.loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <span className="ml-3">Loading record details...</span>
            </div>
          ) : recordModal.data ? (
            <div className="space-y-6 mt-4">
              {/* Record Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-green-500/10 border border-green-500/20 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-500">{recordModal.data.record_summary?.wins || 0}</p>
                  <p className="text-xs text-muted-foreground">Wins</p>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-red-500">{recordModal.data.record_summary?.losses || 0}</p>
                  <p className="text-xs text-muted-foreground">Losses</p>
                </div>
                <div className="bg-primary/10 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-primary">{recordModal.data.record_summary?.win_rate || 0}%</p>
                  <p className="text-xs text-muted-foreground">Win Rate</p>
                </div>
                <div className={`p-3 rounded-lg text-center ${
                  recordModal.data.streak_info?.current_streak?.type === 'W' 
                    ? 'bg-green-500/10 border border-green-500/20' 
                    : 'bg-red-500/10 border border-red-500/20'
                }`}>
                  <p className={`text-2xl font-bold ${
                    recordModal.data.streak_info?.current_streak?.type === 'W' ? 'text-green-500' : 'text-red-500'
                  }`}>
                    {recordModal.data.streak_info?.current_streak?.type || '-'}{recordModal.data.streak_info?.current_streak?.count || 0}
                  </p>
                  <p className="text-xs text-muted-foreground">Current Streak</p>
                </div>
              </div>

              {/* Streak Info */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-secondary/30 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="w-4 h-4 text-green-500" />
                    <span className="text-sm font-medium">Longest Win Streak</span>
                  </div>
                  <p className="text-xl font-bold">{recordModal.data.streak_info?.longest_win_streak || 0} games</p>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingDown className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-medium">Longest Loss Streak</span>
                  </div>
                  <p className="text-xl font-bold">{recordModal.data.streak_info?.longest_loss_streak || 0} games</p>
                </div>
              </div>

              {/* Analysis */}
              {recordModal.data.analysis && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-secondary/30 p-3 rounded-lg text-center">
                    <p className="text-lg font-bold text-green-500">+{recordModal.data.analysis.avg_win_margin || 0}</p>
                    <p className="text-xs text-muted-foreground">Avg Win Margin</p>
                  </div>
                  <div className="bg-secondary/30 p-3 rounded-lg text-center">
                    <p className="text-lg font-bold text-red-500">{recordModal.data.analysis.avg_loss_margin || 0}</p>
                    <p className="text-xs text-muted-foreground">Avg Loss Margin</p>
                  </div>
                  {recordModal.data.analysis.most_beaten_opponent && (
                    <div className="bg-green-500/10 p-3 rounded-lg text-center">
                      <p className="text-sm font-bold">{recordModal.data.analysis.most_beaten_opponent.name}</p>
                      <p className="text-xs text-muted-foreground">{recordModal.data.analysis.most_beaten_opponent.times}x beaten</p>
                    </div>
                  )}
                  {recordModal.data.analysis.most_lost_to_opponent && (
                    <div className="bg-red-500/10 p-3 rounded-lg text-center">
                      <p className="text-sm font-bold">{recordModal.data.analysis.most_lost_to_opponent.name}</p>
                      <p className="text-xs text-muted-foreground">{recordModal.data.analysis.most_lost_to_opponent.times}x lost to</p>
                    </div>
                  )}
                </div>
              )}

              {/* Filter Tabs */}
              <div className="flex gap-2">
                <Button 
                  variant={recordModal.type === 'wins' ? 'default' : 'outline'} 
                  size="sm"
                  className={recordModal.type === 'wins' ? 'bg-green-600 hover:bg-green-700' : ''}
                  onClick={() => fetchRecordDetails(recordModal.team, 'wins')}
                >
                  Wins ({recordModal.data.record_summary?.wins || 0})
                </Button>
                <Button 
                  variant={recordModal.type === 'losses' ? 'default' : 'outline'} 
                  size="sm"
                  className={recordModal.type === 'losses' ? 'bg-red-600 hover:bg-red-700' : ''}
                  onClick={() => fetchRecordDetails(recordModal.team, 'losses')}
                >
                  Losses ({recordModal.data.record_summary?.losses || 0})
                </Button>
              </div>

              {/* Season Performance Graph */}
              {recordModal.data.graph_data && recordModal.data.graph_data.length > 0 && (
                <div className="p-4 bg-secondary/20 rounded-lg">
                  <h4 className="font-medium mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-primary" />
                    Season Performance Trend
                  </h4>
                  <ResponsiveContainer width="100%" height={200}>
                    <ComposedChart data={recordModal.data.graph_data}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis 
                        dataKey="game_num" 
                        tick={{ fontSize: 10, fill: '#888' }}
                        label={{ value: 'Game #', position: 'insideBottom', offset: -5, fill: '#888', fontSize: 10 }}
                      />
                      <YAxis 
                        yAxisId="left"
                        tick={{ fontSize: 10, fill: '#888' }}
                        label={{ value: 'Wins/Losses', angle: -90, position: 'insideLeft', fill: '#888', fontSize: 10 }}
                      />
                      <YAxis 
                        yAxisId="right"
                        orientation="right"
                        domain={[0, 100]}
                        tick={{ fontSize: 10, fill: '#888' }}
                        label={{ value: 'Win %', angle: 90, position: 'insideRight', fill: '#888', fontSize: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#888' }}
                        formatter={(value, name) => {
                          if (name === 'win_rate') return [`${value}%`, 'Win Rate'];
                          if (name === 'cumulative_wins') return [value, 'Total Wins'];
                          if (name === 'cumulative_losses') return [value, 'Total Losses'];
                          return [value, name];
                        }}
                        labelFormatter={(label) => {
                          const game = recordModal.data.graph_data.find(g => g.game_num === label);
                          return game ? `Game ${label}: ${game.result} vs ${game.opponent}` : `Game ${label}`;
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="cumulative_wins" 
                        fill="#22c55e" 
                        fillOpacity={0.3}
                        stroke="#22c55e"
                        name="Total Wins"
                      />
                      <Area 
                        yAxisId="left"
                        type="monotone" 
                        dataKey="cumulative_losses" 
                        fill="#ef4444" 
                        fillOpacity={0.3}
                        stroke="#ef4444"
                        name="Total Losses"
                      />
                      <Line 
                        yAxisId="right"
                        type="monotone" 
                        dataKey="win_rate" 
                        stroke="#8b5cf6" 
                        strokeWidth={2}
                        dot={false}
                        name="Win %"
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Win rate trend over {recordModal.data.graph_data.length} games this season
                  </p>
                </div>
              )}

              {/* Games List */}
              <div className="space-y-2 max-h-[300px] overflow-y-auto">
                {(recordModal.data.games || []).map((game, idx) => (
                  <div 
                    key={game.game_id || idx}
                    className={`p-3 rounded-lg border flex items-center justify-between ${
                      recordModal.type === 'wins' 
                        ? 'bg-green-500/5 border-green-500/20' 
                        : 'bg-red-500/5 border-red-500/20'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Badge 
                        variant={recordModal.type === 'wins' ? 'default' : 'destructive'} 
                        className={`w-8 justify-center ${recordModal.type === 'wins' ? 'bg-green-600' : ''}`}
                      >
                        {recordModal.type === 'wins' ? 'W' : 'L'}
                      </Badge>
                      {game.opponent_logo && (
                        <img src={game.opponent_logo} alt="" className="w-8 h-8 object-contain" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {game.home_away === 'home' ? 'vs' : '@'} {game.opponent}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {game.date ? new Date(game.date).toLocaleDateString('en-US', { 
                            month: 'short', day: 'numeric', year: 'numeric' 
                          }) : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-mono text-sm font-bold">{game.score}</p>
                        <p className={`text-xs ${
                          game.margin > 0 ? 'text-green-500' : game.margin < 0 ? 'text-red-500' : 'text-muted-foreground'
                        }`}>
                          {game.margin > 0 ? '+' : ''}{game.margin}
                        </p>
                      </div>
                      {game.espn_url && (
                        <a 
                          href={game.espn_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-muted-foreground hover:text-primary"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Show wins/losses in "all" mode */}
                {recordModal.type === 'all' && recordModal.data.wins && (
                  <>
                    <h4 className="font-medium mt-4 mb-2 text-green-500">Recent Wins</h4>
                    {recordModal.data.wins.slice(0, 5).map((game, idx) => (
                      <div 
                        key={`win-${game.game_id || idx}`}
                        className="p-3 rounded-lg border bg-green-500/5 border-green-500/20 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <Badge className="w-8 justify-center bg-green-600">W</Badge>
                          <div>
                            <p className="font-medium text-sm">
                              {game.home_away === 'home' ? 'vs' : '@'} {game.opponent}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {game.date ? new Date(game.date).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                        <span className="font-mono text-sm font-bold">{game.score}</span>
                      </div>
                    ))}
                    
                    <h4 className="font-medium mt-4 mb-2 text-red-500">Recent Losses</h4>
                    {recordModal.data.losses?.slice(0, 5).map((game, idx) => (
                      <div 
                        key={`loss-${game.game_id || idx}`}
                        className="p-3 rounded-lg border bg-red-500/5 border-red-500/20 flex items-center justify-between"
                      >
                        <div className="flex items-center gap-3">
                          <Badge variant="destructive" className="w-8 justify-center">L</Badge>
                          <div>
                            <p className="font-medium text-sm">
                              {game.home_away === 'home' ? 'vs' : '@'} {game.opponent}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {game.date ? new Date(game.date).toLocaleDateString() : ''}
                            </p>
                          </div>
                        </div>
                        <span className="font-mono text-sm font-bold">{game.score}</span>
                      </div>
                    ))}
                  </>
                )}
              </div>

              {/* ESPN Link */}
              {recordModal.data.espn_team_url && (
                <div className="pt-4 border-t border-border">
                  <a 
                    href={recordModal.data.espn_team_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View Full Schedule on ESPN
                  </a>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No record data available</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
